import React, { useState, useMemo } from 'react';
import { useRestoMod } from '../../context/RestoModContext';
import {
  SubAssembly,
  SubAssemblyStatus,
  VehicleSystem,
  RestorationPhase,
  RESTORATION_PHASES,
} from '../../types/teardown';
import { MechanicalButton } from '../common/MechanicalButton';
import {
  Box,
  Plus,
  Search,
  Filter,
  Package,
  Wrench,
  CheckCircle2,
  Trash2,
  Edit2,
  ArrowRight,
  AlertTriangle,
  X,
  ExternalLink,
} from 'lucide-react';

export interface SubAssemblyGridProps {
  phaseFilter?: RestorationPhase;
  onSelectSubAssembly?: (subAssembly: SubAssembly) => void;
  onManageHardwareBags?: (subAssembly: SubAssembly) => void;
  onAddHardwareBag?: (subAssembly: SubAssembly) => void;
  className?: string;
}

const SYSTEM_OPTIONS: { id: VehicleSystem; label: string }[] = [
  { id: 'chassis', label: 'Chassis & Frame' },
  { id: 'suspension', label: 'Suspension' },
  { id: 'steering', label: 'Steering Linkage' },
  { id: 'brakes', label: 'Braking System' },
  { id: 'engine', label: 'Engine & Shortblock' },
  { id: 'transmission', label: 'Transmission / Overdrive' },
  { id: 'drivetrain', label: 'Drivetrain & Rear Axle' },
  { id: 'body', label: 'Body & Sheet Metal' },
  { id: 'paint', label: 'Paint & Primer' },
  { id: 'electrical', label: 'Electrical & Harnesses' },
  { id: 'interior', label: 'Interior & Gauges' },
  { id: 'exhaust', label: 'Exhaust & Headers' },
  { id: 'cooling', label: 'Cooling & Radiator' },
  { id: 'fuel', label: 'Fuel System & Lines' },
  { id: 'wheels_tires', label: 'Wheels & Tires' },
  { id: 'glass_trim', label: 'Glass, Trim & Brightwork' },
];

const STATUS_PROGRESSION: SubAssemblyStatus[] = [
  'cataloged',
  'disassembled',
  'cleaning_prep',
  'restoring',
  'ready_for_install',
  'installed',
];

const STATUS_CONFIG: Record<
  SubAssemblyStatus,
  { label: string; badgeClass: string; borderClass: string }
> = {
  cataloged: {
    label: 'Cataloged',
    badgeClass: 'bg-shop-darkest text-shop-muted border-shop-border',
    borderClass: 'border-shop-border',
  },
  disassembled: {
    label: 'Disassembled',
    badgeClass: 'bg-safety-red/20 text-safety-red border-safety-red/40',
    borderClass: 'border-safety-red/50',
  },
  cleaning_prep: {
    label: 'Cleaning & Prep',
    badgeClass: 'bg-safety-amber/20 text-safety-amber border-safety-amber/40',
    borderClass: 'border-safety-amber/50',
  },
  restoring: {
    label: 'Restoring / Overhaul',
    badgeClass: 'bg-safety-blue/20 text-safety-blue border-safety-blue/40',
    borderClass: 'border-safety-blue/50',
  },
  ready_for_install: {
    label: 'Ready For Install',
    badgeClass: 'bg-safety-cyan/20 text-safety-cyan border-safety-cyan/40',
    borderClass: 'border-safety-cyan/50',
  },
  installed: {
    label: 'Installed on Vehicle',
    badgeClass: 'bg-safety-green/20 text-safety-green border-safety-green/40',
    borderClass: 'border-safety-green/50',
  },
};

export const SubAssemblyGrid: React.FC<SubAssemblyGridProps> = ({
  phaseFilter,
  onSelectSubAssembly,
  onManageHardwareBags,
  onAddHardwareBag,
  className = '',
}) => {
  const { state, dispatch } = useRestoMod();
  const activePhase = phaseFilter || state.activePhase;

  // Local filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSystem, setSelectedSystem] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [filterByPhase, setFilterByPhase] = useState<boolean>(true);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSubAssembly, setEditingSubAssembly] = useState<SubAssembly | null>(null);
  const [deletingSubAssembly, setDeletingSubAssembly] = useState<SubAssembly | null>(null);

  // Form input states
  const [formBinNumber, setFormBinNumber] = useState('');
  const [formName, setFormName] = useState('');
  const [formPhase, setFormPhase] = useState<RestorationPhase>(activePhase);
  const [formSystem, setFormSystem] = useState<VehicleSystem>('chassis');
  const [formStatus, setFormStatus] = useState<SubAssemblyStatus>('cataloged');
  const [formLocation, setFormLocation] = useState('');
  const [formNotes, setFormNotes] = useState('');

  // Filtered sub-assemblies list
  const filteredSubAssemblies = useMemo(() => {
    return state.subAssemblies.filter((sub) => {
      // Phase match
      if (filterByPhase && sub.phase !== activePhase) {
        return false;
      }
      // System match
      if (selectedSystem !== 'all' && sub.system !== selectedSystem) {
        return false;
      }
      // Status match
      if (selectedStatus !== 'all' && sub.status !== selectedStatus) {
        return false;
      }
      // Search term match
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchesBin = sub.binNumber.toLowerCase().includes(q);
        const matchesName = sub.name.toLowerCase().includes(q);
        const matchesLocation = sub.storageLocation.toLowerCase().includes(q);
        const matchesNotes = sub.notes.toLowerCase().includes(q);
        return matchesBin || matchesName || matchesLocation || matchesNotes;
      }
      return true;
    });
  }, [state.subAssemblies, filterByPhase, activePhase, selectedSystem, selectedStatus, searchTerm]);

  // Open Add modal with clean state
  const handleOpenAddModal = () => {
    // Generate next bin number suggestion
    const nextNum = state.subAssemblies.length + 1;
    const formattedNum = String(nextNum).padStart(2, '0');
    setFormBinNumber(`BIN-${formattedNum}`);
    setFormName('');
    setFormPhase(activePhase);
    setFormSystem('chassis');
    setFormStatus('cataloged');
    setFormLocation('Shop Rack 1');
    setFormNotes('');
    setIsAddModalOpen(true);
  };

  // Open Edit modal with existing data
  const handleOpenEditModal = (sub: SubAssembly) => {
    setEditingSubAssembly(sub);
    setFormBinNumber(sub.binNumber);
    setFormName(sub.name);
    setFormPhase(sub.phase);
    setFormSystem(sub.system);
    setFormStatus(sub.status);
    setFormLocation(sub.storageLocation);
    setFormNotes(sub.notes);
  };

  // Save New Sub-Assembly
  const handleSaveNewSubAssembly = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formBinNumber.trim() || !formName.trim()) return;

    const newSub: SubAssembly = {
      id: `sub_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      projectId: state.project.id,
      phase: formPhase,
      binNumber: formBinNumber.trim().toUpperCase(),
      name: formName.trim(),
      system: formSystem,
      status: formStatus,
      storageLocation: formLocation.trim() || 'Unassigned',
      notes: formNotes.trim(),
      hardwareBagCount: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    dispatch({ type: 'ADD_SUB_ASSEMBLY', payload: newSub });
    setIsAddModalOpen(false);
  };

  // Save Edited Sub-Assembly
  const handleSaveEditSubAssembly = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSubAssembly || !formBinNumber.trim() || !formName.trim()) return;

    const updated: SubAssembly = {
      ...editingSubAssembly,
      binNumber: formBinNumber.trim().toUpperCase(),
      name: formName.trim(),
      phase: formPhase,
      system: formSystem,
      status: formStatus,
      storageLocation: formLocation.trim() || 'Unassigned',
      notes: formNotes.trim(),
      updatedAt: new Date().toISOString(),
    };

    dispatch({ type: 'UPDATE_SUB_ASSEMBLY', payload: updated });
    setEditingSubAssembly(null);
  };

  // Cycle status forward
  const handleAdvanceStatus = (sub: SubAssembly) => {
    const currentIndex = STATUS_PROGRESSION.indexOf(sub.status);
    const nextIndex = (currentIndex + 1) % STATUS_PROGRESSION.length;
    const nextStatus = STATUS_PROGRESSION[nextIndex];

    dispatch({
      type: 'UPDATE_SUB_ASSEMBLY',
      payload: {
        ...sub,
        status: nextStatus,
        updatedAt: new Date().toISOString(),
      },
    });
  };

  // Confirm delete sub-assembly
  const handleConfirmDelete = () => {
    if (!deletingSubAssembly) return;
    dispatch({ type: 'DELETE_SUB_ASSEMBLY', payload: deletingSubAssembly.id });
    setDeletingSubAssembly(null);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 1. Header Toolbar with Filter & Add Button */}
      <div className="bg-shop-panel border border-shop-border rounded p-4 shadow-machined flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        {/* Search & Phase Toggle */}
        <div className="flex items-center gap-3 flex-wrap flex-1">
          {/* Search Input */}
          <div className="relative min-w-[200px] flex-1 max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-shop-muted pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search bins, names, racks..."
              className="w-full min-h-[44px] pl-9 pr-11 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light placeholder:text-shop-muted focus:outline-none focus:border-safety-amber focus:ring-1 focus:ring-safety-amber font-mono"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-0 top-1/2 -translate-y-1/2 min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-light cursor-pointer"
                aria-label="Clear search"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Phase Filter Toggle */}
          <button
            type="button"
            onClick={() => setFilterByPhase(!filterByPhase)}
            className={`min-h-[44px] px-3 py-2 rounded text-xs font-mono font-bold uppercase border transition-all duration-100 flex items-center gap-2 cursor-pointer ${
              filterByPhase
                ? 'bg-safety-amber text-shop-darkest border-safety-amberDark shadow-sm'
                : 'bg-shop-dark text-shop-muted border-shop-border hover:text-shop-light hover:bg-shop-well'
            }`}
          >
            <Filter className="w-3.5 h-3.5" />
            <span>{filterByPhase ? `Current Phase (${RESTORATION_PHASES[activePhase]?.shortCode})` : 'All Phases'}</span>
          </button>

          {/* System Select */}
          <div className="relative">
            <select
              value={selectedSystem}
              onChange={(e) => setSelectedSystem(e.target.value)}
              className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
            >
              <option value="all">All Systems</option>
              {SYSTEM_OPTIONS.map((sys) => (
                <option key={sys.id} value={sys.id}>
                  {sys.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Select */}
          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-xs text-shop-light focus:outline-none focus:border-safety-amber font-mono cursor-pointer"
            >
              <option value="all">All Statuses</option>
              {STATUS_PROGRESSION.map((st) => (
                <option key={st} value={st}>
                  {STATUS_CONFIG[st].label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Add Sub-Assembly Button (Min 44px) */}
        <MechanicalButton
          variant="primary"
          size="default"
          icon={<Plus className="w-4 h-4" />}
          onClick={handleOpenAddModal}
        >
          Add Storage Bin
        </MechanicalButton>
      </div>

      {/* 2. Sub-Assembly Cards Grid */}
      {filteredSubAssemblies.length === 0 ? (
        <div className="bg-shop-dark border border-shop-border rounded p-12 text-center space-y-3">
          <div className="w-12 h-12 bg-shop-panel rounded-full flex items-center justify-center mx-auto text-shop-muted border border-shop-border">
            <Box className="w-6 h-6" />
          </div>
          <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
            No Storage Bins Found
          </h3>
          <p className="text-xs text-shop-muted max-w-md mx-auto">
            {searchTerm || selectedSystem !== 'all' || selectedStatus !== 'all'
              ? 'No sub-assembly storage bins match your active filter criteria. Try clearing the filters.'
              : `No sub-assembly bins registered for Phase ${RESTORATION_PHASES[activePhase]?.sequence} yet. Create your first physical bin to organize parts and hardware baggies.`}
          </p>
          <div className="pt-2">
            <MechanicalButton variant="secondary" size="sm" onClick={handleOpenAddModal}>
              Create New Bin
            </MechanicalButton>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredSubAssemblies.map((sub) => {
            const statusMeta = STATUS_CONFIG[sub.status];
            // Hardware bag linkages
            const linkedBags = state.hardwareBags.filter((b) => b.subAssemblyId === sub.id);
            const totalFasteners = linkedBags.reduce(
              (acc, b) => acc + b.fasteners.reduce((fAcc, f) => fAcc + f.quantityOriginal, 0),
              0
            );
            const replacementRequiredCount = linkedBags.reduce(
              (acc, b) =>
                acc +
                b.fasteners.filter((f) => f.replacementRequired).reduce((fAcc, f) => fAcc + f.quantityOriginal, 0),
              0
            );

            return (
              <div
                key={sub.id}
                className={`bg-shop-dark border ${statusMeta.borderClass} rounded p-4 space-y-3 shadow-machined flex flex-col justify-between transition-all duration-150 hover:bg-shop-panel`}
              >
                {/* Top: Bin Number, Status, & Actions */}
                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs font-bold text-safety-amber bg-shop-darkest px-2.5 py-1 rounded border border-shop-border shadow-inner">
                        {sub.binNumber}
                      </span>
                      <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-shop-well text-shop-muted border border-shop-border">
                        {sub.system.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleOpenEditModal(sub)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-shop-muted hover:text-shop-highlight hover:bg-shop-well transition-colors"
                        title="Edit storage container"
                        aria-label="Edit bin"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeletingSubAssembly(sub)}
                        className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-shop-muted hover:text-safety-red hover:bg-shop-well transition-colors"
                        title="Delete storage container"
                        aria-label="Delete bin"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Sub-Assembly Name */}
                  <h4
                    onClick={() => onSelectSubAssembly && onSelectSubAssembly(sub)}
                    className="text-sm font-bold text-shop-highlight uppercase tracking-wide cursor-pointer hover:text-safety-amber transition-colors line-clamp-2"
                  >
                    {sub.name}
                  </h4>

                  {/* Physical Storage Location */}
                  <div className="text-xs font-mono text-shop-muted flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-safety-amber shrink-0" />
                    <span className="truncate">Location: <strong className="text-shop-light">{sub.storageLocation}</strong></span>
                  </div>

                  {/* Notes */}
                  {sub.notes && (
                    <p className="text-xs text-shop-muted bg-shop-darkest/70 p-2 rounded border border-shop-border/60 line-clamp-2">
                      {sub.notes}
                    </p>
                  )}
                </div>

                {/* Middle: Hardware Baggie Linkage Summary */}
                <div className="bg-shop-darkest border border-shop-border rounded p-2.5 space-y-2">
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-shop-muted uppercase flex items-center gap-1.5">
                      <Wrench className="w-3.5 h-3.5 text-safety-amber" />
                      Hardware Baggies ({linkedBags.length})
                    </span>
                    {replacementRequiredCount > 0 ? (
                      <span className="text-[10px] font-bold text-safety-red flex items-center gap-1 bg-safety-red/10 px-1.5 py-0.5 rounded border border-safety-red/30">
                        <AlertTriangle className="w-3 h-3" />
                        {replacementRequiredCount} replace
                      </span>
                    ) : (
                      <span className="text-[10px] text-safety-green font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        {totalFasteners} fasteners
                      </span>
                    )}
                  </div>

                  {linkedBags.length > 0 ? (
                    <div className="space-y-1">
                      {linkedBags.slice(0, 2).map((bag) => (
                        <div
                          key={bag.id}
                          className="text-[11px] font-mono text-shop-light bg-shop-panel/80 px-2 py-1 rounded flex items-center justify-between border border-shop-border/40"
                        >
                          <span className="truncate mr-2">{bag.bagLabel}</span>
                          <span className="text-[10px] text-safety-amber uppercase shrink-0">
                            {bag.status.replace('_', ' ')}
                          </span>
                        </div>
                      ))}
                      {linkedBags.length > 2 && (
                        <div className="text-[10px] font-mono text-shop-muted text-center pt-0.5">
                          +{linkedBags.length - 2} more baggies
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-[11px] text-shop-muted italic py-0.5">
                      No hardware baggies linked to this bin yet.
                    </div>
                  )}

                  {/* Manage Hardware Baggies Button */}
                  <div className="pt-1 flex gap-2">
                    <button
                      type="button"
                      onClick={() => onManageHardwareBags && onManageHardwareBags(sub)}
                      className="min-h-[44px] flex-1 bg-shop-panel hover:bg-shop-well text-shop-light border border-shop-border rounded text-xs font-mono font-bold uppercase flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                    >
                      <ExternalLink className="w-3.5 h-3.5 text-safety-amber" />
                      <span>Baggie Specs ({linkedBags.length})</span>
                    </button>
                    {onAddHardwareBag && (
                      <button
                        type="button"
                        onClick={() => onAddHardwareBag(sub)}
                        className="min-h-[44px] px-3 bg-shop-well hover:bg-shop-steel text-safety-amber border border-shop-border rounded text-xs font-mono font-bold flex items-center justify-center transition-colors cursor-pointer"
                        title="Add hardware baggie to this bin"
                        aria-label="Add baggie"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Bottom: Status Advancement Strip */}
                <div className="pt-2 border-t border-shop-border flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-shop-muted uppercase">Lifecycle:</span>
                    <span
                      className={`text-xs font-mono font-bold px-2 py-0.5 rounded border uppercase ${statusMeta.badgeClass}`}
                    >
                      {statusMeta.label}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAdvanceStatus(sub)}
                    className="min-h-[44px] px-3 bg-shop-darkest hover:bg-shop-well text-shop-light border border-shop-border rounded text-xs font-mono flex items-center gap-1 transition-colors cursor-pointer"
                    title="Advance lifecycle status"
                  >
                    <span>Advance</span>
                    <ArrowRight className="w-3.5 h-3.5 text-safety-amber" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* =========================================================================
          3. ADD SUB-ASSEMBLY MODAL (Non-glassmorphic, solid workshop styling)
         ========================================================================= */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-safety-amber rounded max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <Box className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  New Sub-Assembly Storage Bin
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveNewSubAssembly} className="space-y-4 text-xs font-mono">
              <div className="grid grid-cols-2 gap-3">
                {/* Bin Number */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">
                    Bin / Shelf Identifier *
                  </label>
                  <input
                    type="text"
                    required
                    value={formBinNumber}
                    onChange={(e) => setFormBinNumber(e.target.value)}
                    placeholder="e.g. BIN-C02, SHELF-3"
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                  />
                </div>

                {/* Restoration Phase */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              {/* Sub-Assembly Name */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Sub-Assembly Description / Name *
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Front Upper & Lower Control Arms"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Vehicle System */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Vehicle System *</label>
                  <select
                    value={formSystem}
                    onChange={(e) => setFormSystem(e.target.value as VehicleSystem)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {SYSTEM_OPTIONS.map((sys) => (
                      <option key={sys.id} value={sys.id}>
                        {sys.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Initial Status */}
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Initial Status *</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as SubAssemblyStatus)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {STATUS_PROGRESSION.map((st) => (
                      <option key={st} value={st}>
                        {STATUS_CONFIG[st].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Storage Location */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Shop Physical Storage Location *
                </label>
                <input
                  type="text"
                  required
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                  placeholder="e.g. Shop Rack 2, Shelf B, Bay 4"
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              {/* Notes */}
              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Technical Notes</label>
                <textarea
                  rows={3}
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Observations, disassembly order, missing bushings, reassembly specs..."
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              {/* Actions */}
              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Storage Bin
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          4. EDIT SUB-ASSEMBLY MODAL
         ========================================================================= */}
      {editingSubAssembly && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-shop-dark border-2 border-shop-border rounded max-w-lg w-full p-6 space-y-4 shadow-2xl relative">
            <div className="flex items-center justify-between border-b border-shop-border pb-3">
              <div className="flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-safety-amber" />
                <h3 className="text-sm font-bold uppercase tracking-wider text-shop-highlight">
                  Edit Sub-Assembly: {editingSubAssembly.binNumber}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setEditingSubAssembly(null)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center text-shop-muted hover:text-shop-highlight"
                aria-label="Close dialog"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveEditSubAssembly} className="space-y-4 text-xs font-mono">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">
                    Bin / Shelf Identifier *
                  </label>
                  <input
                    type="text"
                    required
                    value={formBinNumber}
                    onChange={(e) => setFormBinNumber(e.target.value)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light font-bold focus:border-safety-amber focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Restoration Phase *</label>
                  <select
                    value={formPhase}
                    onChange={(e) => setFormPhase(e.target.value as RestorationPhase)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {Object.keys(RESTORATION_PHASES).map((pk) => {
                      const p = pk as RestorationPhase;
                      return (
                        <option key={p} value={p}>
                          Phase {RESTORATION_PHASES[p].sequence}: {RESTORATION_PHASES[p].title}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Sub-Assembly Description / Name *
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Vehicle System *</label>
                  <select
                    value={formSystem}
                    onChange={(e) => setFormSystem(e.target.value as VehicleSystem)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {SYSTEM_OPTIONS.map((sys) => (
                      <option key={sys.id} value={sys.id}>
                        {sys.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-shop-muted uppercase block">Status *</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as SubAssemblyStatus)}
                    className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                  >
                    {STATUS_PROGRESSION.map((st) => (
                      <option key={st} value={st}>
                        {STATUS_CONFIG[st].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">
                  Shop Physical Storage Location *
                </label>
                <input
                  type="text"
                  required
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-shop-muted uppercase block">Technical Notes</label>
                <textarea
                  rows={3}
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  className="w-full px-3 py-2 bg-shop-darkest border border-shop-border rounded text-shop-light focus:border-safety-amber focus:outline-none font-sans"
                />
              </div>

              <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
                <MechanicalButton
                  type="button"
                  variant="outline"
                  size="default"
                  onClick={() => setEditingSubAssembly(null)}
                >
                  Cancel
                </MechanicalButton>
                <MechanicalButton type="submit" variant="primary" size="default">
                  Save Changes
                </MechanicalButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
          5. DELETE CONFIRMATION MODAL
         ========================================================================= */}
      {deletingSubAssembly && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-shop-dark border-2 border-safety-red rounded max-w-md w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center gap-3 text-safety-red">
              <AlertTriangle className="w-6 h-6" />
              <h3 className="text-base font-bold uppercase tracking-wider">
                Delete Storage Bin?
              </h3>
            </div>
            <p className="text-xs text-shop-light leading-relaxed">
              Are you sure you want to delete container{' '}
              <strong className="text-safety-amber font-mono font-bold">
                {deletingSubAssembly.binNumber}
              </strong>{' '}
              ({deletingSubAssembly.name})? All linked hardware baggies will also be removed from the system.
            </p>
            <div className="pt-2 flex justify-end gap-3 border-t border-shop-border">
              <MechanicalButton
                type="button"
                variant="outline"
                size="default"
                onClick={() => setDeletingSubAssembly(null)}
              >
                Cancel
              </MechanicalButton>
              <MechanicalButton
                type="button"
                variant="danger"
                size="default"
                onClick={handleConfirmDelete}
              >
                Delete Bin &amp; Baggies
              </MechanicalButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
